import {
  DataType,
  EnumOptionInput,
  EnumSetFieldsFragment,
} from '~/common/generated/apollo-operations'
import { ColumnFormValues, EnumOptionFormValue } from './types'

/** An option as the column read fragment returns it — the icon's id and name both come from here */
type EnumSetOption = NonNullable<EnumSetFieldsFragment['enumSet']>['options'][number]

// Keys only need to be unique within one editor instance, and must stay stable once assigned
let newOptionCount = 0

export const newEnumOptionFormValue = (): EnumOptionFormValue => {
  newOptionCount += 1
  return {
    rowId: `new:${newOptionCount}`,
    isSaved: false,
    label: '',
    colorPreset: null,
    colorCustom: null,
    iconId: null,
    iconName: null,
  }
}

export const isEnumColumnFormValues = (
  column: Pick<ColumnFormValues, 'type' | 'innerType'>,
): boolean => column.type === DataType.ENUM || column.innerType === DataType.ENUM

/**
 * Hydrates editor values from a column's option set, falling back to the flat label mirror for columns
 * that have no set yet.
 *
 * Keys are derived from the column rather than generated: the form re-maps the column on every render
 * and compares the result against form state to decide whether there are unsaved changes, so a fresh
 * key each render would leave the form permanently dirty and reinitialize over the user's edits.
 */
export const enumOptionFormValuesFromColumn = (
  setOptions: EnumSetOption[] | null | undefined,
  labels: string[] | null | undefined,
): EnumOptionFormValue[] => {
  if (setOptions && setOptions.length > 0) {
    return [...setOptions]
      .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id))
      .map((option) => ({
        rowId: option.id,
        isSaved: true,
        label: option.label,
        colorPreset: option.colorPreset ?? null,
        colorCustom: option.colorCustom ?? null,
        iconId: option.icon?.id ?? null,
        iconName: option.iconName ?? null,
      }))
  }

  return (labels ?? []).map((label) => ({
    rowId: `label:${label}`,
    isSaved: true,
    label,
    colorPreset: null,
    colorCustom: null,
    iconId: null,
    iconName: null,
  }))
}

/** Drops the client-only fields; the array order carries each option's order */
export const toEnumOptionInputs = (options: EnumOptionFormValue[]): EnumOptionInput[] =>
  options.map(({ label, colorPreset, colorCustom, iconId }) => ({
    label,
    colorPreset,
    colorCustom,
    iconId,
  }))

export const enumOptionLabels = (options: EnumOptionFormValue[]): string[] =>
  options.map((option) => option.label)
